import { StyledDivider } from '@/components/Shared/Divider';
import CommonModal from '@/features/captable/components/Shared/CommonModal';
import { StyledButton } from '@/lib/theme/components/Button';
import { StyledInput } from '@/lib/theme/components/Input';
import themeToken from '@lib/theme/tokens/index';
import { Divider, Flex, Form, Space, Typography } from 'antd';
import { useGetHighestInterestOffer } from '../api/getHighestInterestOffer';
import { useExpressInterest } from '../api/expressInterest';
import { StyledForm } from '@/components/Shared/Form';
import { useTranslation } from 'react-i18next';
import SharedModal from '@/components/Shared/SharedModal';
import { useState } from 'react';

const { Title, Text } = Typography;

type InterestedModalProps = {
  isModalOpen: boolean;
  handleModalCancel: () => void;
  selectedShareID?: number;
  originalSharePrice?: number;
  selectedCompanyID?: number;
};

const InterestedModal = ({
  isModalOpen,
  handleModalCancel,
  selectedShareID,
  selectedCompanyID,
  originalSharePrice,
}: InterestedModalProps) => {
  const { data: highestInterestOffer } = useGetHighestInterestOffer({
    companyID: selectedCompanyID,
    saleShareID: selectedShareID as number,
  });

  const { t } = useTranslation('marketPlace');

  const [form] = Form.useForm();

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

  const { mutate: expressInterest, isPending: isExpressInterestLoading } = useExpressInterest({
    saleShareID: selectedShareID || -1,
    companyID: selectedCompanyID || -1,
    postSuccess() {
      handleModalCancel();
      form.resetFields();
    },
  });

  const handleExpressInterest = () => {
    const values: { counterOffer?: number } = form.getFieldsValue() as { counterOffer?: number };
    const counterOfferValue = values.counterOffer;
    expressInterest({
      saleShareID: selectedShareID || -1,
      companyID: selectedCompanyID || -1,
      counter_offer: counterOfferValue ? counterOfferValue : null,
    });
  };

  const openConfirmationModal = () => {
    setIsConfirmationModalOpen(true);
  };

  return (
    <>
      <CommonModal isModalOpen={isModalOpen} handleCancel={handleModalCancel}>
        <Title
          style={{
            color: themeToken.colorPrimary,
          }}
          level={5}
        >
          {t('sellInformation')}
        </Title>
        <StyledDivider
          style={{
            marginBlock: themeToken.size,
          }}
        />
        <Space direction="vertical" style={{ width: '100%', padding: '0px 8px' }} size="large">
          <Space
            align="center"
            style={{
              width: '100%',
              justifyContent: 'space-between',
            }}
          >
            <Text type="secondary">{t('highestOfferedPrice')}</Text>
            <Text strong>
              {highestInterestOffer?.counter_offer?.toLocaleString() || originalSharePrice?.toLocaleString()}
            </Text>
          </Space>

          <Divider style={{ margin: '8px 0 0' }} />
          <StyledForm
            form={form}
            layout="vertical"
            disabled={isExpressInterestLoading}
            onFinish={openConfirmationModal}
          >
            <Form.Item name="counterOffer" label={t('counterOffer')}>
              <StyledInput autoFocus style={{ width: '100%' }} min={1} step={1} placeholder="e.g., 1,050" />
            </Form.Item>
            <Flex justify="end" gap={8}>
              <StyledButton onClick={handleModalCancel}>{t('cancel')}</StyledButton>
              <StyledButton type="primary" htmlType="submit" style={{ marginRight: themeToken.size }}>
                {t('submit')}
              </StyledButton>
            </Flex>
          </StyledForm>
        </Space>
      </CommonModal>
      <SharedModal
        open={isConfirmationModalOpen}
        setConfirmDeleteOpen={setIsConfirmationModalOpen}
        title={t('areYouSure')}
        content={<Text>{t('confirmModalContent')}</Text>}
        confirmationText={t('confirm')}
        onOk={() => handleExpressInterest()}
      />
    </>
  );
};

export default InterestedModal;
